import { BIOMES, type BiomeId } from '@goodtime/shared';
import { lightLevel } from './ambient';
import { clampInto, fitRect, shiftOf, type Rect } from './labels';
import { CANDY } from './candyMap';
import { COVE } from './coveMap';
import { EGYPT } from './egyptMap';
import { FRONTIER } from './frontierMap';
import { FROST } from './frostMap';
import { KINGDOM } from './kingdomMap';
import { NEON } from './neonMap';
import type { Pt, WorldBuilding, WorldMapSpec } from './worldSpec';
export { KINGDOM_TILES } from './kingdomMap';
export type { Pt, StillArea, WorldBuilding, WorldMapSpec } from './worldSpec';

/**
 * Each world's own painted map (flag `world_maps`): its tiles, its seven
 * buildings in the world's slot order (WORLD_NODES), where their plates
 * hang, and its road. Points are in the painting's master pixels. A world
 * listed here opens its map from the realm map; any other keeps its board.
 * Pure data and maths, so it is tested without a browser.
 */

/** The most room a world map's plate takes on screen, in CSS pixels (the
 *  plate shrinks its words to stay inside it). */
export const WORLD_PLATE = { w: 172, h: 45 } as const;

/** A narrow plate (a building's `plateNarrow`, at the tight zoom only): the
 *  most room it takes on screen, in CSS pixels. */
export const WORLD_PLATE_NARROW = { w: 128, h: 45 } as const;

/** A world map's plate fades with the share of its building's outline box that is on screen: hidden at
 *  `hidden` or less, fully shown at `shown` or more, and a linear ramp between, so it never pops. A plate then
 *  never labels a building that is mostly off screen (it would read as a neighbour's second name). */
export const WORLD_PLATE_FADE = { hidden: 0.3, shown: 0.4 } as const;

/** The opacity (0 to 1) a world map's plate fades to, for the share of its building's outline box on screen. */
export function plateFade(share: number): number {
  const { hidden, shown } = WORLD_PLATE_FADE;
  return Math.max(0, Math.min(1, (share - hidden) / (shown - hidden)));
}

/** A world map's plate that the usual placing would hide (its spot has left the screen, or the controls would push it
 *  too far) is put where it fits, while its building is wholly on screen: a phone centred on a building must show that
 *  building's own plate at any zoom. The painting is dense, so a plate hangs up to 490 master px from its building, and
 *  with the building centred its spot leaves the view as soon as the player zooms in. `share` is the least share of the
 *  building's outline box on screen (a building that is all but whole in view; the plate fades below
 *  {@link WORLD_PLATE_FADE}.shown, so a half-seen building never has its plate pulled in). `reach` is the most the plate
 *  may move from where it wanted to hang, and how far its spot may be off screen: 340 CSS px. A sweep of every world,
 *  building, size and zoom from the opening zoom to 1.0 (worldPlates.test.ts) needs 327 at most (Neon, zoomed right in on a
 *  phone). The plate then sits over its own building, and never on another one ({@link pullWorldPlate}). */
export const WORLD_PLATE_PULL = { share: 0.95, reach: 340 } as const;

/**
 * Where a world map's plate hangs when at least {@link WORLD_PLATE_PULL}.share of its building is on screen and the usual
 * placing would hide it: its spot is off screen (by no more than {@link WORLD_PLATE_PULL}.reach), or the controls would
 * push it more than `maxShift` from where it wants to hang. The plate is then put where it fits: slid inside the view
 * (`edge` from each side) and clear of the controls (`keepOut`), with its spot moved with it, to be placed by
 * `placeLabels` as any plate (it never moves further than `reach` from where it wanted to hang). Null when the usual placing shows it, or none of that
 * holds. Also null when the plate would land on another building (`clear`, their tap and outline shapes), so the rule
 * never puts a plate on the wrong building: it only ever shows a plate that was hidden. Rects are in screen pixels.
 */
export function pullWorldPlate(
  want: Rect,
  spot: { x: number; y: number },
  view: { w: number; h: number },
  edge: number,
  maxShift: number,
  share: number,
  keepOut: readonly Rect[],
  clear: () => readonly (readonly Pt[])[],
): { want: Rect; spot: { x: number; y: number } } | null {
  if (share < WORLD_PLATE_PULL.share) return null;
  const area = { x: edge, y: edge, w: view.w - 2 * edge, h: view.h - 2 * edge };
  const placed = fitRect(clampInto(want, area), area, keepOut);
  if (!placed) return null;
  const off = Math.max(-spot.x, spot.x - view.w, -spot.y, spot.y - view.h, 0);
  // The usual placing shows it.
  if (off === 0 && shiftOf(placed, want) <= maxShift) return null;
  if (off > WORLD_PLATE_PULL.reach || shiftOf(placed, want) > WORLD_PLATE_PULL.reach) return null;
  if (clear().some((poly) => boxMeetsPolygon(placed, poly))) return null;
  return { want: placed, spot: { x: placed.x - (want.x - spot.x), y: placed.y - (want.y - spot.y) } };
}

/** Nothing tappable in the outer 6% of each side, the top 10% or the bottom
 *  12% of a world's painting: the screen's edges and the HUD. */
export const WORLD_HUD_ZONES = { side: 0.06, top: 0.1, bottom: 0.12 } as const;

/** Every world with its own map in this build: one line for each world,
 *  its data in a file of its own (kingdomMap.ts), its shape in worldSpec.ts. */
export const WORLD_MAPS: Readonly<Partial<Record<BiomeId, WorldMapSpec>>> = {
  kingdom: KINGDOM,
  cove: COVE,
  frost: FROST,
  frontier: FRONTIER,
  neon: NEON,
  egypt: EGYPT,
  candy: CANDY,
};

/** A world's map (by index into BIOMES), or null when it has none yet. */
export function worldMapOf(world: number): WorldMapSpec | null {
  const id = BIOMES[world]?.id;
  return (id && WORLD_MAPS[id]) || null;
}

/** The worlds (indexes into BIOMES) with their own map. */
export function worldsWithMaps(): number[] {
  return BIOMES.flatMap((b, i) => (WORLD_MAPS[b.id] ? [i] : []));
}

// ---- shapes ----

/** Whether a point is inside a polygon. */
export function insidePolygon(p: Pt, poly: readonly Pt[]): boolean {
  const [x, y] = p;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [ax, ay] = poly[i]!;
    const [bx, by] = poly[j]!;
    if (ay > y !== by > y && x < ax + ((bx - ax) * (y - ay)) / (by - ay)) inside = !inside;
  }
  return inside;
}

/** A rectangle in master pixels. */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

function crosses(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const side = (p: Pt, q: Pt, r: Pt): number =>
    (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
  return side(a, b, c) * side(a, b, d) < 0 && side(c, d, a) * side(c, d, b) < 0;
}

/** Whether a rectangle and a polygon overlap. */
export function boxMeetsPolygon(r: Box, poly: readonly Pt[]): boolean {
  const corners: Pt[] = [
    [r.x, r.y],
    [r.x + r.w, r.y],
    [r.x + r.w, r.y + r.h],
    [r.x, r.y + r.h],
  ];
  if (corners.some((c) => insidePolygon(c, poly))) return true;
  if (poly.some(([x, y]) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h)) return true;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    for (let k = 0; k < 4; k++) if (crosses(a, b, corners[k]!, corners[(k + 1) % 4]!)) return true;
  }
  return false;
}

/** Where a tap opens a building: its padded `tap` shape when the spec has
 *  one, otherwise its outline. */
export function tapAreaOf(b: WorldBuilding): readonly Pt[] {
  return b.tap ?? b.outline;
}

/** The distance from a point to the nearest point of a polyline. */
export function distanceToPolyline(p: Pt, line: readonly Pt[]): number {
  let best = Infinity;
  for (let i = 0; i + 1 < line.length; i++) {
    const [ax, ay] = line[i]!;
    const [bx, by] = line[i + 1]!;
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - ay) * dy) / len2));
    best = Math.min(best, Math.hypot(p[0] - ax - t * dx, p[1] - ay - t * dy));
  }
  return best;
}

/** An outline's bounding box. */
export function boundsOf(poly: readonly Pt[]): Box {
  const xs = poly.map((p) => p[0]);
  const ys = poly.map((p) => p[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

// ---- plates ----

/** Whether plates hang at their tight spots at this zoom: a plate then
 *  covers more of the painting than the usual spots leave room for. */
export function platesTight(spec: WorldMapSpec, zoom: number): boolean {
  return WORLD_PLATE.w / Math.max(zoom, 1e-6) > spec.plateBox[0] + 0.5;
}

/** A building's plate's top centre at this zoom. */
export function plateSpot(spec: WorldMapSpec, b: WorldBuilding, zoom: number): Pt {
  return platesTight(spec, zoom) ? b.plateTight : b.plate;
}

/** Whether a building's plate is its narrow two-line one at this zoom. */
export function plateIsNarrow(spec: WorldMapSpec, b: WorldBuilding, zoom: number): boolean {
  return !!b.plateNarrow && platesTight(spec, zoom);
}

/** The painting a plate covers at this zoom, hanging from its spot. */
export function plateBoxAt(spec: WorldMapSpec, b: WorldBuilding, zoom: number): Box {
  const [x, y] = plateSpot(spec, b, zoom);
  const size = plateIsNarrow(spec, b, zoom) ? WORLD_PLATE_NARROW : WORLD_PLATE;
  const w = size.w / zoom;
  const h = size.h / zoom;
  return { x: x - w / 2, y, w, h };
}

/** Every plate's most room on the painting, at the usual zooms and at the
 *  tight zoom (a narrow plate's at its own box): what the ambient life
 *  keeps off. */
export function plateKeepOuts(spec: WorldMapSpec): Box[] {
  return spec.buildings.flatMap((b) => {
    const [nw, nh] = spec.plateBoxNarrow ?? spec.plateBoxTight;
    const [tw, th] = b.plateNarrow ? [nw, nh] : spec.plateBoxTight;
    return [
      { x: b.plate[0] - spec.plateBox[0] / 2, y: b.plate[1], w: spec.plateBox[0], h: spec.plateBox[1] },
      { x: b.plateTight[0] - tw / 2, y: b.plateTight[1], w: tw, h: th },
    ];
  });
}

// ---- the road ----

/** The road's height (master y) at x, by its centre line. */
export function roadY(spec: WorldMapSpec, x: number): number {
  const r = spec.road;
  for (let i = 0; i + 1 < r.length; i++) {
    const [ax, ay] = r[i]!;
    const [bx, by] = r[i + 1]!;
    if (x >= ax && x <= bx) return bx === ax ? ay : ay + ((by - ay) * (x - ax)) / (bx - ax);
  }
  return x < r[0]![0] ? r[0]![1] : r[r.length - 1]![1];
}

/** How far along the road (master pixels from its start) the point of it
 *  nearest `p` is. */
export function alongRoad(spec: WorldMapSpec, p: Pt): number {
  let best = Infinity;
  let at = 0;
  let s = 0;
  const r = spec.road;
  for (let i = 0; i + 1 < r.length; i++) {
    const [ax, ay] = r[i]!;
    const [bx, by] = r[i + 1]!;
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy);
    const t = Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - ay) * dy) / (len * len || 1)));
    const d = Math.hypot(p[0] - ax - t * dx, p[1] - ay - t * dy);
    if (d < best) {
      best = d;
      at = s + t * len;
    }
    s += len;
  }
  return at;
}

/** The point `s` master pixels along the road, and the road's direction
 *  there (a unit vector). */
export function roadAt(spec: WorldMapSpec, s: number): { p: Pt; dir: Pt } {
  const r = spec.road;
  let left = Math.max(0, s);
  for (let i = 0; i + 1 < r.length; i++) {
    const [ax, ay] = r[i]!;
    const [bx, by] = r[i + 1]!;
    const len = Math.hypot(bx - ax, by - ay);
    if (left <= len || i + 2 === r.length) {
      const t = Math.min(1, left / (len || 1));
      return { p: [ax + (bx - ax) * t, ay + (by - ay) * t], dir: [(bx - ax) / len, (by - ay) / len] };
    }
    left -= len;
  }
  return { p: r[0]!, dir: [1, 0] };
}

export function roadLength(spec: WorldMapSpec): number {
  let s = 0;
  for (let i = 0; i + 1 < spec.road.length; i++) {
    const [ax, ay] = spec.road[i]!;
    const [bx, by] = spec.road[i + 1]!;
    s += Math.hypot(bx - ax, by - ay);
  }
  return s;
}

/** Where each building meets the road: how far along it, in slot order. */
export function buildingStops(spec: WorldMapSpec): number[] {
  return spec.buildings.map((b) => alongRoad(spec, b.centre));
}

/** A lantern by the road. */
export interface Lantern {
  x: number;
  y: number;
  /** How far along the road it stands. */
  s: number;
  /** The painting's own lit lamp (the spec's `lanterns`): only its glow is
   *  drawn, in code. Otherwise a post and lamp are drawn too. */
  painted: boolean;
}

/** Lanterns between them, along the road. */
export const LANTERN_SPACING = 170;

/**
 * The road's lanterns: one every {@link LANTERN_SPACING} along it, on
 * alternate verges, none on a building, a still area (the bridge) or within
 * the painting's outer 2%. They are drawn in code where the painting has
 * none. A world whose painting has its own lamp posts lists their feet in
 * its spec's `lanterns`, and those stand instead, in order along the road.
 * A world whose painting has none lists the feet of the posts the code
 * draws in `lanternPosts`, chosen on open ground, and those stand instead.
 */
export function roadLanterns(spec: WorldMapSpec, spacing = LANTERN_SPACING): Lantern[] {
  if (spec.lanterns) {
    return spec.lanterns
      .map(([x, y]): Lantern => ({ x, y, s: alongRoad(spec, [x, y]), painted: true }))
      .sort((a, b) => a.s - b.s);
  }
  if (spec.lanternPosts) {
    return spec.lanternPosts
      .map(([x, y]): Lantern => ({ x, y, s: alongRoad(spec, [x, y]), painted: false }))
      .sort((a, b) => a.s - b.s);
  }
  const out: Lantern[] = [];
  const total = roadLength(spec);
  const off = spec.roadWidth / 2 + 16;
  const { width, height } = spec.tiles;
  let side = 1;
  for (let s = spacing / 2; s < total; s += spacing) {
    const { p, dir } = roadAt(spec, s);
    side = -side;
    const pt: Pt = [p[0] - dir[1] * off * side, p[1] + dir[0] * off * side];
    if (pt[0] < width * 0.02 || pt[0] > width * 0.98 || pt[1] < 0 || pt[1] > height) continue;
    if (spec.buildings.some((b) => insidePolygon(pt, b.outline))) continue;
    if (spec.still.some((a) => insidePolygon(pt, a.outline))) continue;
    out.push({ x: pt[0], y: pt[1], s, painted: false });
  }
  return out;
}

/**
 * How far along the road the lanterns are lit, given which slots are lit
 * (by building, in slot order): to the furthest lit building's stop plus 40.
 * A finished world, every slot lit, lights the road to its end, so the
 * lamps past the last building (a world whose road runs on beyond it) are
 * not left dark.
 */
export function roadLitTo(spec: WorldMapSpec, lit: readonly boolean[]): number {
  if (lit.length > 0 && lit.length === spec.buildings.length && lit.every(Boolean)) return Infinity;
  const stops = buildingStops(spec);
  let to = 0;
  lit.forEach((on, i) => {
    if (on) to = Math.max(to, stops[i]! + 40);
  });
  return to;
}

/** A lit lantern's flame: a soft, slow flicker, never a strobe (the
 *  ambient lights' `flicker`, kept bright). */
export const LANTERN_FLICKER = { mode: 'flicker', period: 2.8, low: 0.74 } as const;

/** How bright lit lantern `i` of `count` burns at time `t` (seconds), from
 *  LANTERN_FLICKER's low to 1, each on its own. */
export function lanternFlicker(i: number, count: number, t: number): number {
  return lightLevel(LANTERN_FLICKER, t, i, count);
}

/** The building nearest a point of the painting (fractions of it), by its
 *  middle, across the painting rather than down it (the road runs left to
 *  right). */
export function nearestBuilding(spec: WorldMapSpec, fx: number, fy: number): number {
  const x = fx * spec.tiles.width;
  const y = fy * spec.tiles.height;
  let best = 0;
  let bestD = Infinity;
  spec.buildings.forEach((b, i) => {
    const d = Math.hypot(b.centre[0] - x, (b.centre[1] - y) * 0.35);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}
